import { useEffect, useMemo, useState } from 'react';
import 'jsoneditor-react/es/editor.min.css';
import ace from 'brace';
import 'brace/mode/json';
import 'brace/theme/monokai';
import 'brace/ext/searchbox';

import Stack from 'rsuite/Stack';
import Button from 'rsuite/Button';
import Message from 'rsuite/Message';
import Loader from 'rsuite/Loader';
import { useToaster } from 'rsuite/esm/toaster';

// @ts-ignore - typings are incomplete in this package
import { JsonEditor } from 'jsoneditor-react';

import './PrepListSettings.css';
import { useStores } from './hooks/useStores';
import { useUpdateStores } from './hooks/useUpdateStores';

type Store = {
  id?: number | string;
  [key: string]: unknown;
};

type StoresApiResponse = {
  stores: Store[];
};

type StoreEditorItem = Omit<Store, 'id'>;

type StoresEditorValue = {
  stores: StoreEditorItem[];
};

type AppError = {
  message?: string;
};

type ValidationResult = {
  valid: boolean;
  errors: string[];
};

type JsonEditorValidationError = {
  path?: Array<string | number>;
  message?: string;
};

type JsonEditorCodeProps = {
  value: unknown;
  mode?: 'code' | 'tree';
  ace?: typeof ace;
  theme?: string;
  navigationBar?: boolean;
  statusBar?: boolean;
  allowedModes?: Array<'code' | 'tree'>;
  onChangeText?: (jsonText: string) => void;
  onValidationError?: (errors: JsonEditorValidationError[]) => void;
};

const TypedJsonEditor = JsonEditor as unknown as React.ComponentType<JsonEditorCodeProps>;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const validateStoresJson = (value: unknown): ValidationResult => {
  const errors: string[] = [];

  if (!isRecord(value)) {
    return {
      valid: false,
      errors: ['Root value must be a JSON object.']
    };
  }

  if (!('stores' in value)) {
    errors.push("Missing required property 'stores'.");
    return {
      valid: false,
      errors
    };
  }

  const { stores } = value;

  if (!Array.isArray(stores)) {
    errors.push("'stores' must be an array.");
    return {
      valid: false,
      errors
    };
  }

  stores.forEach((item, index) => {
    if (!isRecord(item)) {
      errors.push(`stores[${index}] must be an object.`);
      return;
    }

    // Optional example checks. Adjust these to match your real store schema.
    if ('name' in item && typeof item.name !== 'string') {
      errors.push(`stores[${index}].name must be a string.`);
    }

    if ('code' in item && typeof item.code !== 'string') {
      errors.push(`stores[${index}].code must be a string.`);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
};

const formatJson = (value: unknown): string => {
  return JSON.stringify(value, null, 2);
};

const tryParseJson = (
  text: string
): { success: true; value: unknown } | { success: false; error: string } => {
  try {
    return {
      success: true,
      value: JSON.parse(text)
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Invalid JSON syntax.'
    };
  }
};

const StoresAdmin = (): JSX.Element => {
  /**
   * Initial value provided to JsonEditor.
   * We seed the editor once from API data.
   */
  const [editorSeed, setEditorSeed] = useState<StoresEditorValue | null>(null);

  /**
   * Exact raw JSON text currently in the editor.
   */
  const [rawJsonText, setRawJsonText] = useState<string>('');

  /**
   * Current parsed JSON only when syntax + validation are both valid.
   * This is what we save.
   */
  const [parsedJson, setParsedJson] = useState<StoresEditorValue | null>(null);

  /**
   * Syntax error from invalid JSON in code mode.
   */
  const [syntaxError, setSyntaxError] = useState<string | null>(null);

  /**
   * Validation errors after successful JSON parse.
   */
  const [schemaErrors, setSchemaErrors] = useState<string[]>([]);

  const {
    data,
    isLoading,
    error,
    refetch
  } = useStores() as {
    data?: StoresApiResponse;
    isLoading: boolean;
    error?: unknown;
    refetch: () => void;
  };

  const {
    mutate: updateStores,
    isLoading: savingInProgress
  } = useUpdateStores() as {
    mutate: (
      payload: StoresEditorValue,
      options?: {
        onSuccess?: () => void;
        onError?: () => void;
      }
    ) => void;
    isLoading: boolean;
  };

  const toaster = useToaster();

  useEffect(() => {
    if (!data?.stores || !Array.isArray(data.stores)) {
      return;
    }

    const storesWithoutId: StoresEditorValue = {
      stores: data.stores.map(({ id, ...storeWithoutId }) => storeWithoutId)
    };

    setEditorSeed(storesWithoutId);
    setRawJsonText(formatJson(storesWithoutId));
    setParsedJson(storesWithoutId);
    setSyntaxError(null);

    const validation = validateStoresJson(storesWithoutId);
    setSchemaErrors(validation.errors);
  }, [data]);

  const handleChangeText = (jsonText: string): void => {
    setRawJsonText(jsonText);

    const parsed = tryParseJson(jsonText);

    if (!parsed.success) {
      setSyntaxError(parsed.error);
      setParsedJson(null);
      setSchemaErrors([]);
      return;
    }

    setSyntaxError(null);

    const validation = validateStoresJson(parsed.value);
    setSchemaErrors(validation.errors);

    if (validation.valid) {
      setParsedJson(parsed.value as StoresEditorValue);
    } else {
      setParsedJson(null);
    }
  };

  const handleEditorValidationError = (errors: JsonEditorValidationError[]): void => {
    // Optional hook from jsoneditor-react. You can use this if needed,
    // but tryParseJson() remains the source of truth for syntax handling.
    if (!errors || errors.length === 0) {
      return;
    }
  };

  const canSave = useMemo(() => {
    return !savingInProgress && !syntaxError && schemaErrors.length === 0 && parsedJson !== null;
  }, [savingInProgress, syntaxError, schemaErrors, parsedJson]);

  const handleSave = (): void => {
    if (syntaxError) {
      toaster.push(
        <Message showIcon type="error" closable>
          Cannot save because the JSON syntax is invalid.
        </Message>,
        { placement: 'topEnd', duration: 4000 }
      );
      return;
    }

    if (!parsedJson) {
      toaster.push(
        <Message showIcon type="warning" closable>
          No valid store data is available to save.
        </Message>,
        { placement: 'topEnd', duration: 4000 }
      );
      return;
    }

    const validation = validateStoresJson(parsedJson);

    if (!validation.valid) {
      setSchemaErrors(validation.errors);

      toaster.push(
        <Message showIcon type="error" closable>
          Cannot save. Please fix the validation errors first.
        </Message>,
        { placement: 'topEnd', duration: 4000 }
      );
      return;
    }

    updateStores(parsedJson, {
      onSuccess: () => {
        toaster.push(
          <Message showIcon type="success" closable>
            Stores saved successfully
          </Message>,
          { placement: 'topEnd', duration: 3000 }
        );

        refetch();
      },
      onError: () => {
        toaster.push(
          <Message showIcon type="error" closable>
            Error while saving stores. Please try again.
          </Message>,
          { placement: 'topEnd', duration: 3000 }
        );
      }
    });
  };

  if (isLoading) {
    return <Loader center />;
  }

  if (error) {
    const errorMessage =
      typeof error === 'object' && error !== null && 'message' in error
        ? (error as AppError).message
        : 'Unknown error';

    return (
      <div className="alert alert-danger" role="alert">
        Error loading stores: {errorMessage}
      </div>
    );
  }

  if (!editorSeed) {
    return <Loader center />;
  }

  return (
    <div>
      <TypedJsonEditor
        value={editorSeed}
        onChangeText={handleChangeText}
        onValidationError={handleEditorValidationError}
        mode="code"
        ace={ace}
        theme="ace/theme/monokai"
        navigationBar={true}
        statusBar={true}
        allowedModes={['code', 'tree']}
      />

      {syntaxError && (
        <div className="alert alert-danger" style={{ marginTop: '12px' }}>
          <strong>JSON syntax error:</strong>
          <div>{syntaxError}</div>
        </div>
      )}

      {!syntaxError && schemaErrors.length > 0 && (
        <div className="alert alert-danger" style={{ marginTop: '12px' }}>
          <strong>Validation errors:</strong>
          <ul style={{ marginBottom: 0 }}>
            {schemaErrors.map((err, index) => (
              <li key={index}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <Stack style={{ marginTop: '8px', marginBottom: '8px' }} direction="row-reverse">
        <Button onClick={handleSave} appearance="primary" disabled={!canSave}>
          {savingInProgress && <Loader size="sm" style={{ marginRight: '8px' }} />}
          Save
        </Button>
      </Stack>
    </div>
  );
};

export default StoresAdmin;